import { isPast, parseISO } from 'date-fns'
import cookies from '../cookies'
import getCurrentDomain from '../getCurrentDomain'
import { STV_COOKIES, CONTINUE_WATCHING_COUNT } from '../../constants'

const { STV_STOP_RESUME_COOKIE } = STV_COOKIES

export const getStopResumePosition = ({ id }, req) => {
  const stopResumeItem = readCookies(req).find(item => item.id === Number(id))
  if (!stopResumeItem) return 0

  let stopResumePosition
  try {
    stopResumePosition = parseInt(stopResumeItem.pos, 10)
  } catch (error) {
    stopResumePosition = 0
  }

  if (Number.isNaN(stopResumePosition)) return 0

  return stopResumePosition
}

export const readCookies = (req) => {
  let cookie = []
  try {
    if (typeof window !== 'undefined') {
      // Browser environment
      const cookieStr = document.cookie
      const cookieValue = cookieStr
        .split('; ')
        .find(row => row.startsWith(`${STV_STOP_RESUME_COOKIE}=`))
      if (cookieValue) {
        const cookieString = cookieValue.split('=')[1]
        cookie = JSON.parse(atobUTF16(decodeURIComponent(cookieString)))
      }
    } else {
      // Server environment
      const cookieValue = cookies.get(STV_STOP_RESUME_COOKIE, req)
      if (cookieValue) {
        const decodedUriValue = decodeURIComponent(cookieValue)
        cookie = JSON.parse(atobUTF16(decodedUriValue))
      }
    }
  } catch (err) {
    // Return an empty array if parsing fails
    cookie = []
  }
  return cookie
}

export const writeCookies = (cookieArray) => {
  const encoded = btoaUTF16(JSON.stringify(cookieArray))
  const domain = `.${getCurrentDomain()}`
  if (typeof window !== 'undefined') {
    // Browser environment
    document.cookie = `${STV_STOP_RESUME_COOKIE}=${encodeURIComponent(encoded)}; domain=${domain}; path=/; max-age=${30 * 24 * 60 * 60}; ${window.location.protocol === 'https:' ? 'secure;' : ''} SameSite=Lax`
  } else {
    // Server environment
    cookies.set(STV_STOP_RESUME_COOKIE, encoded, {
      domain,
      expires: 30,
      secure: true,
      sameSite: 'Lax',
      path: '/',
    })
  }
}

export const setStopResumePosition = ({
  duration,
  expires,
  id,
  imageId,
  position,
  programmeName,
  summary,
  programmeGuid,
  forceAds,
  svodExclusive,
  sportsExclusive,
}, req) => {
  // Validation: Ensure essential data (ID, Programme GUID, Image ID) is present before proceeding
  if (!id || !programmeGuid || !imageId) {
    return // Do not modify cookies if essential data is invalid/incomplete
  }

  const savedPosition = typeof position === 'number' ? Math.round(position) : 0
  const newValue = {
    dur: duration,
    exp: expires ? expires.split('+')[0] : '',
    id: Number(id),
    img: imageId,
    pos: savedPosition,
    prg: programmeName,
    sum: summary,
    pid: programmeGuid,
    fads: forceAds,
    svodex: svodExclusive,
    sportex: sportsExclusive,
  }
  const currentCookies = readCookies(req)

  const existingIndex = currentCookies.findIndex((item) => {
    const isMatch = item.id === Number(id) && !isPast(parseISO(item.exp))
    return isMatch
  })

  if (existingIndex >= 0) {
    currentCookies[existingIndex] = newValue
  } else {
    currentCookies.unshift(newValue)
  }

  const filteredCookies = currentCookies
    .filter((item) => {
      const expired = isPast(parseISO(item.exp))
      return !expired
    })
    .slice(0, CONTINUE_WATCHING_COUNT)

  writeCookies(filteredCookies)
}

export function clearStopResumePosition({ id }, req) {
  const cookieArray = readCookies(req)
  const index = cookieArray.findIndex(item => Number(item.id) === Number(id))
  if (index !== -1) {
    cookieArray.splice(index, 1)
    writeCookies(cookieArray)
  }
}

export function getRemainingTime({ position, duration }) {
  if (
    typeof position !== 'number'
    || typeof duration !== 'number'
    || position > duration
  ) {
    return 0 // Fail silently, returning 0 minutes remaining
  }
  return duration - position
}

export const parseRemainingTimeString = (milliseconds) => {
  const timeInMinutes = Math.round(milliseconds / 60000)
  if (typeof timeInMinutes !== 'number' || Number.isNaN(timeInMinutes)) return null
  const prefix = timeInMinutes <= 0 ? 'Less than 1' : ''
  const suffix = timeInMinutes <= 1 ? 'minute' : 'minutes'
  return `${prefix}${timeInMinutes === 0 ? '' : timeInMinutes} ${suffix}`
}

// Avoid blowing up when base64 encoding/decoding 16-bit strings
/* eslint-disable no-param-reassign */
export function btoaUTF16(sString) {
  const aUTF16CodeUnits = new Uint16Array(sString.length)
  Array.prototype.forEach.call(aUTF16CodeUnits, (el, idx, arr) => {
    arr[idx] = sString.charCodeAt(idx)
  })
  return typeof btoa !== 'undefined'
    ? btoa(String.fromCharCode.apply(null, new Uint8Array(aUTF16CodeUnits.buffer)))
    : Buffer.from(
      String.fromCharCode.apply(
        null,
        new Uint8Array(aUTF16CodeUnits.buffer)
      )
    ).toString('base64')
}

export function atobUTF16(sBase64) {
  const sBinaryString = typeof atob !== 'undefined'
    ? atob(sBase64)
    : Buffer.from(sBase64, 'base64').toString('binary')
  const aBinaryView = new Uint8Array(sBinaryString.length)
  Array.prototype.forEach.call(aBinaryView, (el, idx, arr) => {
    arr[idx] = sBinaryString.charCodeAt(idx)
  })
  return String.fromCharCode.apply(null, new Uint16Array(aBinaryView.buffer))
}
/* eslint-enable no-param-reassign */
